import { nextTick, ref, unref, watch, type ComputedRef } from 'vue'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import {
  activateContextMenu,
  deactivateContextMenu,
  type ContextMenuInstance,
} from '@/common/utils/contextMenuCoordinator'

interface ContextMenuOptions {
  contextMenuItems?: ComputedRef<any[]> | any[]
  /**
   * Вызывается непосредственно перед show (после onEmit / onInlineMenuShow).
   * Нужен, чтобы не показывать протухший props-снимок.
   */
  getContextMenuItems?: () => any[]
  headers?: ComputedRef<any[]> | any[]
  /** Может быть async: ждём onInlineMenuShow / visibility до show меню. Preview может вернуть items. */
  onEmit?: (event: any) => void | any[] | Promise<void | any[]>
}

/**
 * Helper to unwrap computed refs or return the value
 */
const unwrap = <T>(value: ComputedRef<T> | T): T => {
  return unref(value)
}

/**
 * Reusable composable for table context menu functionality
 */
export function useTableContextMenu(options: ContextMenuOptions = {}) {
  const { contextMenuItems = [], headers = [], onEmit, getContextMenuItems } = options

  const contextMenu = ref<(ContextMenuInstance & { show: (event: MouseEvent) => void }) | null>(null)
  const contextCellValue = ref<string>('')
  const contextRowData = ref<any>(null)
  const contextColumnIndex = ref<number | null>(null)

  // Helper to extract cell text for copying
  const extractCellText = (event: MouseEvent | undefined, rowData: any): string => {
    const target = event?.target as HTMLElement | null
    if (!target || !rowData) return ''

    // Try to find the cell content
    const cell = target.closest('td')
    if (!cell) return ''

    // Get text content from the cell
    const text = cell.textContent?.trim() || ''
    return text
  }

  // Helper to copy text to clipboard
  const copyTextToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  const normalizeMenuIcon = (icon: unknown): string | undefined => {
    if (typeof icon !== 'string') return undefined
    const trimmed = icon.trim()
    if (!trimmed) return undefined
    return getPrimeIcon(trimmed)
  }

  const buildMenuModel = (items: any[]) => {
    const itemsArray = Array.isArray(items) ? items : []
    const baseItems = itemsArray.map(item => {
      const mappedIcon = normalizeMenuIcon(item?.icon)
      return {
        ...item,
        ...(mappedIcon ? { icon: mappedIcon } : {}),
      }
    })

    baseItems.push({
      label: 'Копировать значение',
      icon: 'pi pi-copy',
      command: async () => {
        let valueToCopy = contextCellValue.value

        if (!valueToCopy && contextRowData.value && contextColumnIndex.value != null) {
          const headersList = unwrap(headers)
          const headersArray = Array.isArray(headersList) ? headersList : []
          const header = headersArray[contextColumnIndex.value]
          if (header && header.value) {
            const rawValue = contextRowData.value[header.value]
            if (rawValue !== undefined && rawValue !== null) {
              if (typeof rawValue === 'object' && 'value' in rawValue) {
                valueToCopy = String((rawValue as any).value ?? '')
              } else if (typeof rawValue === 'object') {
                try {
                  valueToCopy = JSON.stringify(rawValue)
                } catch {
                  valueToCopy = String(rawValue)
                }
              } else {
                valueToCopy = String(rawValue)
              }
            }
          }
        }

        if (!valueToCopy && contextRowData.value) {
          if (contextRowData.value._chipLabel) {
            valueToCopy = String(contextRowData.value._chipLabel)
          }

          if (!valueToCopy) {
            const textFields = ['label', 'text', 'description', 'name', 'title', 'fio', 'fullName']
            for (const field of textFields) {
              const fieldValue = contextRowData.value[field]
              if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                valueToCopy = String(fieldValue)
                break
              }
            }
          }

          if (!valueToCopy && contextRowData.value.fields && typeof contextRowData.value.fields === 'object') {
            const textFields = ['label', 'text', 'description', 'name', 'title', 'fio', 'fullName']
            for (const field of textFields) {
              const fieldData = contextRowData.value.fields[field]
              if (fieldData) {
                const fieldValue = fieldData.value ?? fieldData._text
                if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                  valueToCopy = String(fieldValue)
                  break
                }
              }
            }
          }

        }

        await copyTextToClipboard(valueToCopy || '')
      }
    })

    return baseItems
  }

  const resolvePlatformItems = (): any[] => {
    if (typeof getContextMenuItems === 'function') {
      const items = getContextMenuItems()
      return Array.isArray(items) ? items : []
    }
    const items = unwrap(contextMenuItems)
    return Array.isArray(items) ? items : []
  }

  /** Ref model — пересобирается перед show после onInlineMenuShow. */
  const contextMenuModel = ref(buildMenuModel(resolvePlatformItems()))

  const rebuildContextMenuModel = () => {
    contextMenuModel.value = buildMenuModel(resolvePlatformItems())
  }

  watch(
    () => unwrap(contextMenuItems),
    () => {
      rebuildContextMenuModel()
    },
    {deep: true},
  )

  /**
   * onInlineMenuShow + rebuild model. Не показывает меню — для primary-кнопки и ПКМ.
   * Header / нет event → null.
   */
  const prepareContextMenu = async (
    event: any,
    rowData?: any,
    column?: string | number,
    options?: { withCheckbox?: boolean; selectedIdx?: number; preview?: boolean }
  ): Promise<any[] | null> => {
    const originalEvent: MouseEvent | undefined = event?.originalEvent || event
    const target = originalEvent?.target as HTMLElement | null
    const isPreview = options?.preview === true

    if (!originalEvent && !isPreview) {
      return null
    }

    if (target?.closest('thead')) {
      contextCellValue.value = ''
      contextRowData.value = null
      contextColumnIndex.value = null
      return null
    }

    originalEvent?.preventDefault?.()

    const data = rowData || event?.data || null

    let columnIndex: number | null = null
    if (typeof column === 'string') {
      const headersList = unwrap(headers)
      const headersArray = Array.isArray(headersList) ? headersList : []
      columnIndex = headersArray.findIndex((h: any) => h.value === column)
    } else if (typeof column === 'number') {
      columnIndex = column
    } else {
      const cell = target?.closest('td') as HTMLTableCellElement | null
      if (cell && typeof cell.cellIndex === 'number') {
        let headerIndex = cell.cellIndex
        if (options?.withCheckbox) {
          headerIndex -= 1
        }
        columnIndex = headerIndex >= 0 ? headerIndex : null
      }
    }

    if (!isPreview) {
      contextRowData.value = data
      contextCellValue.value = extractCellText(originalEvent, data) || ''
      contextColumnIndex.value = columnIndex
    }

    let columnName = typeof column === 'string' ? column : ''
    if (!columnName && columnIndex != null) {
      const headersList = unwrap(headers)
      const headersArray = Array.isArray(headersList) ? headersList : []
      const header = headersArray[columnIndex]
      if (header && (header as any).value) {
        columnName = String((header as any).value)
      }
    }

    let fromEmit: any[] | undefined
    if (onEmit) {
      const emitted = await onEmit({
        selectedIdx: options?.selectedIdx ?? -1,
        target: originalEvent,
        column: columnName,
        data: data,
        preview: isPreview,
      })
      if (Array.isArray(emitted)) fromEmit = emitted
    }

    if (isPreview) {
      return fromEmit ?? resolvePlatformItems()
    }

    await nextTick()
    if (Array.isArray(fromEmit)) {
      contextMenuModel.value = buildMenuModel(fromEmit)
    } else {
      rebuildContextMenuModel()
    }
    await nextTick()

    return fromEmit ?? resolvePlatformItems()
  }

  const showPreparedContextMenu = (originalEvent: MouseEvent) => {
    if (contextMenuModel.value.length > 0 && contextMenu.value) {
      activateContextMenu(contextMenu.value, originalEvent.target)
      contextMenu.value.show(originalEvent)
    }
  }

  const handleContextMenu = async (
    event: any,
    rowData?: any,
    column?: string | number,
    options?: { withCheckbox?: boolean; selectedIdx?: number }
  ) => {
    const originalEvent: MouseEvent | undefined = event?.originalEvent || event
    const prepared = await prepareContextMenu(event, rowData, column, options)
    if (prepared == null || !originalEvent) {
      return
    }
    showPreparedContextMenu(originalEvent)
  }

  const onContextMenuHide = () => {
    if (contextMenu.value) {
      deactivateContextMenu(contextMenu.value)
    }
  }

  return {
    contextMenu,
    contextMenuModel,
    contextCellValue,
    contextRowData,
    contextColumnIndex,
    prepareContextMenu,
    showPreparedContextMenu,
    handleContextMenu,
    onContextMenuHide,
    extractCellText,
    copyTextToClipboard
  }
}
